import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Col, Form, Row } from 'antd';
import { DatePicker, Select, TextArea, Popup } from '_atoms';
import type { SelectOption } from '_atoms/Select';
import { REQUIRED_RULE } from '_organisms/InterviewFeedback/constants';
import { getUser } from 'store/selectors/authorizeSelectors';
import * as actions from 'actions';
import { ROLES } from 'permission';
import { possibleTime } from 'constants/constants';
import { BUTTON_TEXT } from 'constants/text';
import { useAppDispatch } from 'store';
import type { Moment } from 'moment';
import type { CandidateInteraction, CandidateInteractionPayload } from 'store/reducers/candidateReducer';

interface InteractionFormValues {
  date: Moment;
  time: string;
  recruiter?: string;
  comment: string;
  activity?: string;
  type?: string;
}

const reminderType = [
  { value: 'Phone call', label: 'Phone call' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Email', label: 'Email' },
  { value: 'Telegram', label: 'Telegram' },
  { value: 'Viber', label: 'Viber' },
  { value: 'Interview', label: 'Interview' },
  { value: 'Other interaction', label: 'Other interaction' },
];

const activitiesList = [
  { value: 'Sent an invitation', label: 'Sent an invitation' },
  { value: 'Sent a message', label: 'Sent a message' },
  { value: 'Not interested', label: 'Not interested' },
  { value: 'Ping', label: 'Ping' },
  { value: 'Scheduled HR call', label: 'Scheduled HR call' },
];

interface PopupCreateInteractionProps {
  open: boolean;
  onClose: () => void;
  data?: Partial<CandidateInteraction> | null;
}

export const PopupCreateInteraction: React.FC<PopupCreateInteractionProps> = ({ open, onClose, data }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();

  const user = useSelector(getUser);

  const [recruiterOptions, setRecruiterOptions] = useState<SelectOption[]>([]);

  const initialValues = useMemo(
    () => ({
      date: moment().set({ hour: 13, minute: 0, second: 0 }),
      time: possibleTime[26].value,
      recruiter: user.uuid,
      comment: '',
      activity: undefined,
      type: undefined,
    }),
    [user]
  );

  const onSave = ({ date, time, ...values }: InteractionFormValues) => {
    const hour = Number(time.split(':')[0]);
    const minute = Number(time.split(':')[1]);
    const payload: CandidateInteractionPayload = {
      ...values,
      datetime: date.set({ hour, minute, second: 0 }),
      candidate: id,
      in_progress: true,
    };
    dispatch(actions.addInteractionAPI(payload, data?.uuid, id)).then(onClose);
  };

  useEffect(() => {
    if (open) {
      actions
        .getListUsersByRole({ role: [ROLES.HR_LEAD, ROLES.RECRUITER_LEAD, ROLES.RECRUITER] })
        .then(resp => setRecruiterOptions((resp || []).map(r => ({ label: r.full_name, value: r.uuid }))))
        .catch(() => setRecruiterOptions([]));
    } else {
      setRecruiterOptions([]);
      form.resetFields();
    }
  }, [open]);

  useEffect(() => {
    if (data) form.setFieldsValue(data);
    else form.resetFields();
  }, [data]);

  return (
    <Popup
      open={open}
      title={
        data?.uuid
          ? t('translation:popup_create_interaction.title_edit')
          : t('translation:popup_create_interaction.title_new')
      }
      cancelButtonProps={{ onClick: onClose, children: BUTTON_TEXT.CANCEL }}
      okButtonProps={{
        children: data?.uuid ? BUTTON_TEXT.SAVE : BUTTON_TEXT.CREATE,
        form: 'form-interaction',
        htmlType: 'submit',
      }}
      onClick={onClose}
    >
      <Form name="form-interaction" form={form} initialValues={initialValues} onFinish={onSave}>
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <DatePicker
              label={t('translation:popup_create_interaction.label_date')}
              maxDate={moment().endOf('day')}
              itemProps={{ name: 'date', rules: [REQUIRED_RULE] }}
            />
          </Col>
          <Col span={12}>
            <Select
              label={t('translation:popup_create_interaction.label_time')}
              placeholder={t('translation:popup_create_interaction.placeholder_time')}
              options={possibleTime as SelectOption[]}
              itemProps={{ name: 'time', rules: [REQUIRED_RULE] }}
            />
          </Col>
        </Row>
        <Select
          label={t('translation:popup_create_interaction.label_communication_type')}
          placeholder={t('translation:popup_create_interaction.placeholder_communication_type')}
          options={reminderType}
          itemProps={{ name: 'type', rules: [REQUIRED_RULE] }}
        />
        <Select
          label={t('translation:popup_create_interaction.label_activity')}
          placeholder={t('translation:popup_create_interaction.placeholder_activity')}
          options={activitiesList}
          itemProps={{ name: 'activity' }}
          allowClear
        />
        <Select
          label={t('translation:popup_create_interaction.label_recruiter')}
          placeholder={t('translation:popup_create_interaction.placeholder_recruiter')}
          options={recruiterOptions}
          itemProps={{ name: 'recruiter', rules: [REQUIRED_RULE] }}
          optionFilterProp="label"
        />
        <TextArea
          label={t('translation:popup_create_interaction.label_comment')}
          placeholder={t('translation:popup_create_interaction.placeholder_comment')}
          itemProps={{ name: 'comment' }}
        />
      </Form>
    </Popup>
  );
};
